// ---------------------------------------------------------------------------
// CONTRACTOR RATINGS + RESULTS — single source of truth (Phase 1.1)
// ---------------------------------------------------------------------------
// mkt_contractor_ratings: one structured 1–5 star rating per listing, written by
// the homeowner ONLY when the listing is closed as 'work_completed' and the
// rated contractor is the recorded hired_buyer_id. Enforced twice server side:
//   • RLS p_ratings_ins (owner + work_completed + hired contractor)
//   • trigger mkt_contractor_rating_guard (runs for every insert)
// Trigger mkt_contractor_rating_refresh maintains mkt_profiles.avg_rating and
// rating_count; mkt_profile_jobs_won_refresh maintains mkt_profiles.jobs_won
// (drives the "GigGone Hired" badge). Users can never write those counters
// themselves (mkt_profile_reputation_guard).
// ---------------------------------------------------------------------------
import { supabase } from '@/lib/supabase';

export const RATING_COMMENT_MAX = 1000;

export interface ContractorRating {
  id: string;
  listing_id: string;
  homeowner_id: string;
  contractor_id: string;
  rating: number;
  would_hire_again: boolean | null;
  comment: string | null;
  created_at: string;
}

export interface RatingInput {
  listingId: string;
  homeownerId: string;
  contractorId: string;
  rating: number;
  wouldHireAgain: boolean;
  comment?: string;
}

export const ratingErrorMessage = (msg: string, es: boolean): string => {
  if (/duplicate key|unique/i.test(msg))
    return es ? 'Ya calificaste a este contratista para este proyecto.' : 'You already rated this contractor for this project.';
  if (/rating_requires_work_completed|rating_contractor_not_hired|row-level security/i.test(msg))
    return es
      ? 'Solo puedes calificar al contratista contratado después de marcar el trabajo como terminado.'
      : 'You can only rate the hired contractor after marking the work completed.';
  return es ? 'No se pudo guardar la calificación. Inténtalo de nuevo.' : 'Could not save your rating. Please try again.';
};

export const submitContractorRating = async (p: RatingInput): Promise<{ error?: string }> => {
  const rating = Math.round(p.rating);
  if (rating < 1 || rating > 5) return { error: 'invalid_rating' };
  const { error } = await supabase.from('mkt_contractor_ratings').insert({
    listing_id: p.listingId,
    homeowner_id: p.homeownerId,
    contractor_id: p.contractorId,
    rating,
    would_hire_again: p.wouldHireAgain,
    comment: (p.comment || '').trim().slice(0, RATING_COMMENT_MAX) || null,
  });
  return error ? { error: error.message || 'insert_failed' } : {};
};

/** Ratings readable by the caller (RLS: homeowner who wrote it, the rated contractor, admins). */
export const fetchRatingsForListings = async (listingIds: string[]): Promise<Map<string, ContractorRating>> => {
  if (!listingIds.length) return new Map();
  const { data } = await supabase.from('mkt_contractor_ratings').select('*').in('listing_id', listingIds);
  return new Map(((data || []) as ContractorRating[]).map((r) => [r.listing_id, r]));
};

export interface Reputation {
  avg_rating: number | null;
  rating_count: number;
  jobs_won: number;
}

/** Profile-level reputation counters (visible wherever the profile itself is readable). */
export const fetchReputation = async (ids: string[]): Promise<Map<string, Reputation>> => {
  if (!ids.length) return new Map();
  const { data } = await supabase.from('mkt_profiles').select('id,avg_rating,rating_count,jobs_won').in('id', ids);
  return new Map(
    ((data || []) as any[]).map((p) => [
      p.id,
      { avg_rating: p.avg_rating == null ? null : Number(p.avg_rating), rating_count: Number(p.rating_count || 0), jobs_won: Number(p.jobs_won || 0) },
    ])
  );
};

export const formatAvg = (avg: number | null | undefined) => (avg == null ? '—' : Number(avg).toFixed(1));

export interface MyResults {
  leads_bought: number;
  leads_closed: number;
  jobs_won: number;
  jobs_completed: number;
  completed_job_value: number;
  win_rate_pct: number | null;
}

/** Caller's own row from mkt_contractor_outcome_stats (non-admins only ever get their own row). */
export const fetchMyResults = async (userId: string): Promise<MyResults | null> => {
  const { data, error } = await supabase.rpc('mkt_contractor_outcome_stats', { p_buyer: userId });
  if (error) throw error;
  const row = ((data || []) as any[]).find((r) => r.buyer_id === userId) || (data || [])[0];
  if (!row) return { leads_bought: 0, leads_closed: 0, jobs_won: 0, jobs_completed: 0, completed_job_value: 0, win_rate_pct: null };
  return {
    leads_bought: Number(row.leads_bought || 0),
    leads_closed: Number(row.leads_closed || 0),
    jobs_won: Number(row.jobs_won || 0),
    jobs_completed: Number(row.jobs_completed || 0),
    completed_job_value: Number(row.completed_job_value || 0),
    win_rate_pct: row.win_rate_pct == null ? null : Number(row.win_rate_pct),
  };
};

/**
 * Contractor-side outcome chip for a purchased lead. Only shown once the
 * homeowner recorded an outcome. 'hired' = this contractor was selected;
 * 'not_selected' = the project closed any other way.
 */
export type LeadOutcomeChip = 'hired' | 'not_selected' | null;
export const leadOutcomeChip = (
  listing: { closing_outcome?: string | null; hired_buyer_id?: string | null } | null | undefined,
  myId?: string | null
): LeadOutcomeChip => {
  if (!listing?.closing_outcome) return null;
  return myId && listing.hired_buyer_id === myId ? 'hired' : 'not_selected';
};
